import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { fromEvent, merge, Subscription, timer, BehaviorSubject } from 'rxjs';
import { switchMap, throttleTime, filter, tap } from 'rxjs/operators';
import { SystemService } from '@k12/app-services';

@Injectable({
  providedIn: 'root'
})
export class InactivityMonitorService implements OnDestroy {
  private userActivitySubscription: Subscription = new Subscription();

  private readonly isIdleSubject = new BehaviorSubject<boolean>(false);
  public isIdle$ = this.isIdleSubject.asObservable();

  public readonly isIdleWarning = new BehaviorSubject<boolean>(false);

  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly systemService = inject(SystemService);

  constructor() {}

  /**
   * @param timeoutMinutes Total time before logout/idle state
   * @param warningMinutes Minutes remaining when the warning should appear
   */
  public startTracking(timeoutMinutes: number = 60, warningMinutes: number = 5): void {
    const totalMs = timeoutMinutes * 60 * 1000;
    const warningMs = warningMinutes * 60 * 1000;
    const timeBeforeWarning = totalMs - warningMs;

    const activity$ = merge(
      fromEvent(globalThis, 'mousemove'),
      fromEvent(globalThis, 'keydown'),
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd))
    ).pipe(
      throttleTime(1000),
      tap(() => {
        if (this.isIdleSubject.value) {
          this.ngZone.run(() => this.isIdleSubject.next(false));
        }
      })
    );

    this.ngZone.runOutsideAngular(() => {
      this.userActivitySubscription = activity$
        .pipe(
          switchMap(() => {
            return merge(
              timer(timeBeforeWarning).pipe(tap(this.handleWarningTimer.bind(this))),
              timer(totalMs).pipe(tap(this.handleLogoutTimer.bind(this)))
            );
          })
        )
        // Internal timer/DOM-event stream; cannot error by construction, so no error handler is needed.
        .subscribe();
    });
  }

  private showWarning(): void {
    this.isIdleWarning.next(true);
  }

  private handleWarningTimer(): void {
    this.ngZone.run(() => this.showWarning());
  }

  private handleLogoutTimer(): void {
    this.ngZone.run(() => this.systemService.logout());
  }

  public stopTracking(): void {
    this.userActivitySubscription?.unsubscribe();
  }

  ngOnDestroy(): void {
    this.stopTracking();
  }
}
